import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  MessageCircle,
  Send,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BRAND } from "@/lib/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "Real Estate Developer",
  "Realtor / Broker",
  "Lifestyle / D2C Brand",
  "Hospitality",
  "Coaching Institute",
  "Online Course / EdTech",
  "Local Business",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    business_type: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.business_type) {
      toast.error("Please fill your name, email, phone & business type.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/leads`, form);
      setSubmitted(true);
      toast.success("Thanks! We'll reach out within 24 hours.");
      setForm({
        name: "",
        email: "",
        phone: "",
        business_type: "",
        message: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not submit. Please try WhatsApp.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const waText = encodeURIComponent(
    "Hi Digibis, I'd like to book a free strategy call."
  );

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <div className="text-xs tracking-[0.25em] uppercase font-bold text-[#E3A21A] mb-4">
              Get In Touch
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#F5F5F0] font-medium">
              Let's build your
              <br />
              <span className="text-[#E3A21A] italic font-light">
                revenue system.
              </span>
            </h2>
            <p className="mt-6 text-[#A8B8A8] text-base leading-relaxed max-w-md">
              Tell us about your business. We'll respond within 24 hours with
              next steps and a slot for your free strategy call.
            </p>

            <div className="mt-10 space-y-4">
              <a
                href={BRAND.phoneHref}
                data-testid="contact-phone"
                className="flex items-center gap-4 p-5 rounded-xl border border-[#E3A21A]/10 bg-[#1E2A1E]/50 hover:border-[#E3A21A]/40 hover:bg-[#1E2A1E] transition-all group"
              >
                <div className="h-12 w-12 rounded-lg bg-[#121A12] border border-[#E3A21A]/20 flex items-center justify-center text-[#E3A21A] group-hover:bg-[#E3A21A] group-hover:text-[#121A12] transition-colors">
                  <Phone className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#A8B8A8]">
                    Phone
                  </div>
                  <div className="font-display text-lg text-[#F5F5F0] mt-0.5">
                    {BRAND.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email"
                className="flex items-center gap-4 p-5 rounded-xl border border-[#E3A21A]/10 bg-[#1E2A1E]/50 hover:border-[#E3A21A]/40 hover:bg-[#1E2A1E] transition-all group"
              >
                <div className="h-12 w-12 rounded-lg bg-[#121A12] border border-[#E3A21A]/20 flex items-center justify-center text-[#E3A21A] group-hover:bg-[#E3A21A] group-hover:text-[#121A12] transition-colors">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#A8B8A8]">
                    Email
                  </div>
                  <div className="font-display text-lg text-[#F5F5F0] mt-0.5 break-all">
                    {BRAND.email}
                  </div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-5 rounded-xl border border-[#E3A21A]/10 bg-[#1E2A1E]/50">
                <div className="h-12 w-12 rounded-lg bg-[#121A12] border border-[#E3A21A]/20 flex items-center justify-center text-[#E3A21A]">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#A8B8A8]">
                    Location
                  </div>
                  <div className="font-display text-lg text-[#F5F5F0] mt-0.5">
                    {BRAND.location}
                  </div>
                </div>
              </div>

              <a
                href={`https://wa.me/${BRAND.whatsapp}?text=${waText}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-center justify-between gap-4 p-5 rounded-xl bg-[#E3A21A] text-[#121A12] hover:bg-[#F0B63A] transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-lg bg-[#121A12] text-[#E3A21A] flex items-center justify-center">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase font-bold opacity-70">
                      Instant Reply
                    </div>
                    <div className="font-display text-lg font-semibold mt-0.5">
                      Chat on WhatsApp
                    </div>
                  </div>
                </div>
                <Send className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="relative rounded-3xl border border-[#E3A21A]/15 bg-[#1E2A1E] p-8 md:p-12 overflow-hidden">
              <div className="absolute -top-24 -right-24 h-56 w-56 rounded-full bg-[#E3A21A]/10 blur-3xl pointer-events-none" />

              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="relative z-10 flex flex-col items-center text-center py-16"
                >
                  <div className="h-16 w-16 rounded-full bg-[#E3A21A] text-[#121A12] flex items-center justify-center mb-6">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h3 className="font-display text-3xl text-[#F5F5F0] font-medium">
                    Request received.
                  </h3>
                  <p className="mt-4 text-[#A8B8A8] max-w-md">
                    We'll reach out within 24 hours with a slot for your free
                    strategy call. Meanwhile, you can WhatsApp us for instant
                    reply.
                  </p>
                  <Button
                    data-testid="contact-submit-another"
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="mt-8 rounded-full border-[#E3A21A]/30 text-[#F5F5F0] hover:bg-[#E3A21A]/10 hover:text-[#F5F5F0] hover:border-[#E3A21A]"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={submit}
                  noValidate
                  className="relative z-10 space-y-5"
                >
                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#A8B8A8] mb-2">
                      Full Name
                    </label>
                    <Input
                      data-testid="contact-input-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      placeholder="Rohan Sharma"
                      className="h-12 bg-[#121A12] border-[#E3A21A]/15 text-[#F5F5F0] placeholder:text-[#A8B8A8]/60 focus:border-[#E3A21A] focus-visible:ring-[#E3A21A]/30 rounded-lg"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#A8B8A8] mb-2">
                        Email
                      </label>
                      <Input
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        placeholder="you@company.com"
                        className="h-12 bg-[#121A12] border-[#E3A21A]/15 text-[#F5F5F0] placeholder:text-[#A8B8A8]/60 focus:border-[#E3A21A] focus-visible:ring-[#E3A21A]/30 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#A8B8A8] mb-2">
                        Phone
                      </label>
                      <Input
                        data-testid="contact-input-phone"
                        value={form.phone}
                        onChange={(e) => update("phone", e.target.value)}
                        placeholder="+91 98765 43210"
                        className="h-12 bg-[#121A12] border-[#E3A21A]/15 text-[#F5F5F0] placeholder:text-[#A8B8A8]/60 focus:border-[#E3A21A] focus-visible:ring-[#E3A21A]/30 rounded-lg"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#A8B8A8] mb-2">
                      Business Type
                    </label>
                    <Select
                      value={form.business_type}
                      onValueChange={(v) => update("business_type", v)}
                    >
                      <SelectTrigger
                        data-testid="contact-select-business"
                        className="h-12 bg-[#121A12] border-[#E3A21A]/15 text-[#F5F5F0] focus:border-[#E3A21A] focus:ring-[#E3A21A]/30 data-[placeholder]:text-[#A8B8A8]/60 rounded-lg"
                      >
                        <SelectValue placeholder="Choose your business type" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1E2A1E] border-[#E3A21A]/20 text-[#F5F5F0]">
                        {BUSINESS_TYPES.map((b) => (
                          <SelectItem
                            key={b}
                            value={b}
                            data-testid={`business-type-${b.replace(/\s|\//g, "-").toLowerCase()}`}
                            className="focus:bg-[#E3A21A]/10 focus:text-[#F5F5F0]"
                          >
                            {b}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-xs tracking-[0.2em] uppercase font-bold text-[#A8B8A8] mb-2">
                      What do you want to scale?{" "}
                      <span className="text-[#A8B8A8]/50 normal-case tracking-normal">
                        (optional)
                      </span>
                    </label>
                    <Textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="Share your current challenge, goals, and monthly ad spend if any."
                      rows={4}
                      className="bg-[#121A12] border-[#E3A21A]/15 text-[#F5F5F0] placeholder:text-[#A8B8A8]/60 focus-visible:border-[#E3A21A] focus-visible:ring-[#E3A21A]/30 rounded-lg"
                    />
                  </div>

                  <Button
                    data-testid="contact-submit"
                    type="submit"
                    disabled={submitting}
                    className="w-full h-14 rounded-full bg-[#E3A21A] text-[#121A12] font-semibold text-base hover:bg-[#F0B63A] transition-all shadow-[0_0_30px_rgba(227,162,26,0.25)] disabled:opacity-70"
                  >
                    {submitting ? "Sending…" : "Book Free Strategy Call"}
                    {!submitting && <Send className="ml-2 h-4 w-4" />}
                  </Button>

                  <p className="text-center text-xs text-[#A8B8A8]/80">
                    By submitting, you agree to be contacted about our services.
                    We never spam.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
